<template>
  <div :class="[{ 'block-width': block }, 'button-wrapper']">
    <button
      :class="buttonClasses"
      @click="click()"
      @mouseover="hovering = true"
      @mouseleave="hovering = false">
      <AppLoader v-if="loadingState" />
      <slot v-else />
    </button>

    <AppTooltip
      :shown="tooltip && hovering"
      class="tooltip">
      {{ tooltip }}
    </AppTooltip>
  </div>
</template>

<script>
import AppTooltip from '@/components/ui/AppTooltip'
export default {
  name: 'AppButton',
  components: {
    AppTooltip
  },
  props: {
    specificClass: {
      type: String,
      required: false,
      default: null
    },
    tooltip: {
      type: String,
      required: false,
      default: null
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    },
    type: {
      type: String,
      required: false,
      default: 'default',
      validator: function (input) {
        return (
          [
            'default',
            'success',
            'warning',
            'error',
            'neutral',
            'dracula',
            'ghost-default',
            'ghost-neutral',
            'ghost-warning',
            'ghost-success',
            'ghost-error',
            'link-error',
            'link-default',
            'link-warning',
            'link-success',
            'link-white',
            'link-neutral'
          ].indexOf(input) > -1
        )
      }
    },
    loadingState: {
      type: Boolean,
      required: false,
      default: false
    },
    size: {
      type: String,
      required: false,
      default: 'medium',
      validator: function (value) {
        return ['icon', 'small', 'medium', 'big', 'large'].indexOf(value) > -1
      }
    },
    block: {
      type: Boolean,
      required: false,
      default: false
    },
    rounded: {
      type: Boolean,
      required: false,
      default: false
    },
    square: {
      type: Boolean,
      required: false,
      default: false
    },
    uppercase: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data () {
    return {
      hovering: false
    }
  },
  computed: {
    buttonClasses: function () {
      let classes = ['app-button']
      this.disabled
        ? classes.push(this.type + '--disabled')
        : classes.push(this.type)

      if (this.tooltip) classes.push('has-tooltip')
      if (this.block) classes.push('block-width')
      if (this.rounded) classes.push('rounded')
      if (this.square) classes.push('square')
      if (this.uppercase) classes.push('uppercase')
      if (this.specificClass) classes.push(this.specificClass)
      classes.push(this.size)
      return classes
    }
  },
  methods: {
    click: function () {
      if (!this.disabled) {
        this.$emit('click')
      }
    }
  }
}
</script>

<style lang="scss" scoped>
$types: "default", "success", "warning", "error", "neutral", "white", "dracula";
$colors: (
  'success': $success,
  "default": $default,
  'error': $error,
  'warning': $warning,
  'dracula': $dracula,
  'white': $white,
  'neutral': $neutral-light
);

%disabled {
  background: $neutral-lightest;
  color: $neutral-lighter;
  cursor: default;
}

%link-disabled {
  background: none;
  color: $neutral-lighter;
  cursor: default;
}

%ghost-disabled {
  @extend %link-disabled;
  border: 2px solid $neutral-lighter;

  &:hover {
    background: none;
    color: $neutral-lighter;
  }
}

%border {
  border: 1px solid transparent;
}

%background-none {
  background-color: none;
}

%cursor-anim {
  transition-duration: 0.25s;
  cursor: pointer;
}

.button-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  margin: 0 2px;

  &.block-width {
    width: 100%;
  }
}

.app-button {
  @extend %cursor-anim;
  position: relative;
  border: 0;
  background: none;
  border-radius: 5px;
  color: $neutral-light;
  font-weight: 400;
  font-family: inherit;
  outline: none;
  display: flex;
  align-items: center;
  justify-content: space-between;

  * {
    margin: 0 5px;
  }

  &.comment {
    float: right;
    &.medium{
      padding: 0.5rem 0.5rem 0.5rem 0.8rem;
      margin-left: 0.9rem;
    }
  }

  &.rounded {
    border-radius: 30px;
  }

  &.square {
    border-radius: 0;
  }

  @each $type in $types {
    &.#{$type} {
      @extend %border;

      background-color: map-get($map: $colors, $key: $type);
      color: $neutral-light-absolute;

      &:hover {
        @if #{$type} == 'white' {
          background-color: darken(map-get($map: $colors, $key: $type), 5%);
        } @else {
          background-color: lighten(map-get($map: $colors, $key: $type), 8%);
        }
      }

      &--disabled {
        @extend %disabled;

        cursor: not-allowed;

        &:hover {
          background-color: none;
        }
      }
    }

    &.link-#{$type} {
      @extend %background-none;
      color: map-get($map: $colors, $key: $type);

      &:hover {
        @if #{$type} == 'white' {
          color: darken(map-get($map: $colors, $key: $type), 10%);
        } @else {
          color: lighten(map-get($map: $colors, $key: $type), 8%);
        }
      }

      &--disabled {
        @extend %link-disabled;

        cursor: not-allowed;
      }
    }

    &.ghost-#{$type} {
      @extend %background-none;
      color: map-get($map: $colors, $key: $type);
      border: 2px solid map-get($map: $colors, $key: $type);

      &:hover {
        background-color: map-get($map: $colors, $key: $type);
        color: $neutral-light-absolute;
      }

      &--disabled {
        @extend %ghost-disabled;

        cursor: not-allowed;
      }
    }
  }

  &.icon {
    width: 30px;
    height: 30px;
    font-size: 1rem;
    justify-content: center;
  }

  &.small {
    font-size: 0.825rem;
    padding: 0.3rem 0.7rem;
  }

  &.medium {
    font-size: 0.95rem;
    padding: 0.5rem 1rem;
  }

  &.big {
    font-size: 1.125rem;
    padding: 0.75rem 1.5rem;
  }

  &.large {
    width: 100%;
    padding: 0.75rem 1.5rem;
    display: inline-block;
    font-size: 0.95rem;
  }

  &.block-width {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &.uppercase {
    text-transform: uppercase;
    font-size: 0.85em;
    font-weight: 800;
  }

  &.first-button {
    margin-right: 10px;
  }
}

.rule--project--exclusion .app-button{
  color:$neutral-lightest
}

.tooltip {
  position: absolute;
  top: 100%;
  z-index: 9999;
  display: inline-block;
}
</style>
